import { useCallback, useEffect, useId, useMemo, useRef } from "react";

// One history entry per open layer (same URL, F7's view state kept), so F7's
// router ignores these entries and the back button closes the top layer.
const DEPTH_KEY = "backLayers";

const layers = [];
let awaitingPop = false;
let syncScheduled = false;
let listening = false;

const depthOf = (state) => state?.[DEPTH_KEY] || 0;

const sync = () => {
  syncScheduled = false;
  if (awaitingPop) return;
  const depth = depthOf(window.history.state);
  if (layers.length > depth) {
    for (let next = depth + 1; next <= layers.length; next += 1) {
      window.history.pushState(
        { ...window.history.state, [DEPTH_KEY]: next },
        "",
      );
    }
  } else if (layers.length < depth) {
    awaitingPop = true;
    window.history.go(layers.length - depth);
  }
};

const scheduleSync = () => {
  if (syncScheduled) return;
  syncScheduled = true;
  Promise.resolve().then(sync);
};

const handlePopState = (event) => {
  if (awaitingPop) {
    awaitingPop = false;
  } else {
    const depth = depthOf(event.state);
    while (layers.length > depth && layers[layers.length - 1].canClose()) {
      layers.pop().close();
    }
  }
  sync();
};

const listen = () => {
  if (listening) return;
  listening = true;
  window.addEventListener("popstate", handlePopState);
};

const addLayer = (layer) => {
  listen();
  layers.push(layer);
  scheduleSync();
};

const removeLayer = (layer) => {
  const index = layers.indexOf(layer);
  if (index < 0) return;
  layers.splice(index, 1);
  scheduleSync();
};

export const dropStaleBackEntry = () => {
  if (!depthOf(window.history.state)) return;
  const { [DEPTH_KEY]: _stale, ...state } = window.history.state;
  window.history.replaceState(state, "");
};

dropStaleBackEntry();

const always = () => true;

export const openBackLayer = (close, canClose = always) => {
  const layer = { close, canClose };
  addLayer(layer);
  return () => removeLayer(layer);
};

export const useBackClose = (opened, close, canClose = always) => {
  const latest = useRef({ close, canClose });
  latest.current = { close, canClose };
  const releaseRef = useRef(null);

  useEffect(() => {
    if (!opened) return undefined;
    const release = openBackLayer(
      () => latest.current.close(),
      () => latest.current.canClose(),
    );
    releaseRef.current = release;
    return () => {
      release();
      if (releaseRef.current === release) releaseRef.current = null;
    };
  }, [opened]);

  return useCallback(() => {
    releaseRef.current?.();
    releaseRef.current = null;
  }, []);
};

export const usePopupBackClose = (opened, canClose) => {
  const id = `popup-${useId().replace(/:/g, "")}`;
  const release = useBackClose(
    opened,
    () => document.getElementById(id)?.f7Modal?.close(),
    canClose,
  );
  return useMemo(() => ({ id, onPopupClose: release }), [id, release]);
};
